import { motion } from 'framer-motion';

const sections = [
  {
    icon: 'location_on',
    title: 'Colombia',
    subtitle: 'Recaudo & Pagos Locales',
    color: 'from-purple-primary/15 to-accent-magenta/5',
    border: 'border-purple-primary/25',
    iconColor: 'text-purple-primary',
    methods: [
      { name: 'PSE', img: '/logos/pse.png' },
      { name: 'ACH Colombia', img: '/logos/ach.png' },
      { name: 'Nequi', img: '/logos/nequi.svg' },
      { name: 'Daviplata', img: '/logos/davivienda.svg' },
      { name: 'Transferencias Bancarias', matIcon: 'send_money', iconColor: 'text-purple-primary' },
      { name: 'Tarjetas Crédito/Débito', img: '/logos/visa.svg' },
    ],
  },
  {
    icon: 'language',
    title: 'LATAM & Global',
    subtitle: 'Multimoneda & SWIFT',
    color: 'from-blue-500/10 to-purple-primary/5',
    border: 'border-blue-500/20',
    iconColor: 'text-blue-400',
    methods: [
      { name: 'Dólares (USD)', flag: '🇺🇸' },
      { name: 'Pesos Mexicanos (MXN)', flag: '🇲🇽' },
      { name: 'Pesos Argentinos (ARS)', flag: '🇦🇷' },
      { name: 'Pesos Chilenos (CLP)', flag: '🇨🇱' },
      { name: 'Soles Peruanos (PEN)', flag: '🇵🇪' },
      { name: 'Reales Brasileños (BRL)', flag: '🇧🇷' },
      { name: 'SWIFT Internacional', emoji: '🌐' },
      { name: 'Tarjetas Internacionales', img: '/logos/visa.svg' },
    ],
  },
  {
    icon: 'credit_card',
    title: 'Emisión',
    subtitle: 'Business Black Cards',
    color: 'from-amber-500/10 to-purple-primary/5',
    border: 'border-amber-500/20',
    iconColor: 'text-amber-400',
    methods: [
      { name: 'Tarjeta Débito Digital', matIcon: 'phone_iphone' },
      { name: 'Tarjeta Débito Física', matIcon: 'credit_card' },
      { name: 'Tarjeta Tokenizada', matIcon: 'lock' },
      { name: 'Control de Gastos', matIcon: 'monitoring' },
      { name: 'Cupos Personalizados', matIcon: 'tune' },
      { name: 'Trazabilidad en Tiempo Real', matIcon: 'schedule' },
    ],
  },
];

function MethodIcon({ method }) {
  if (method.img) {
    return (
      <div className="w-10 h-10 rounded-lg bg-white/90 flex items-center justify-center flex-shrink-0 p-1.5">
        <img src={method.img} alt="" className="w-full h-full object-contain select-none" />
      </div>
    );
  }
  if (method.flag) {
    return <span className="text-2xl leading-none">{method.flag}</span>;
  }
  if (method.emoji) {
    return <span className="text-xl leading-none">{method.emoji}</span>;
  }
  if (method.matIcon) {
    return (
      <div className={`w-9 h-9 rounded-lg bg-purple-primary/10 border border-purple-primary/20 flex items-center justify-center flex-shrink-0`}>
        <span className={`material-symbols-outlined ${method.iconColor || 'text-purple-primary'} text-[20px]`}>{method.matIcon}</span>
      </div>
    );
  }
  return null;
}

export default function PaymentMethods() {
  return (
    <section className="py-24 bg-transparent">
      <div className="mx-auto min-w-0 max-w-7xl px-4 sm:px-6">
        <div className="text-center mb-16">
          <motion.h2
            className="text-4xl md:text-5xl font-black font-headline tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Métodos de Pago <span className="gradient-text">Locales y Globales</span>
          </motion.h2>
          <p className="text-on-surface-variant text-lg mt-4 max-w-2xl mx-auto">
            Una sola integración para acceder a todos los métodos de pago que sus clientes necesitan.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {sections.map((section, si) => (
            <motion.div
              key={section.title}
              className={`rounded-2xl border ${section.border} bg-gradient-to-b ${section.color} overflow-hidden`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ delay: si * 0.15, duration: 0.6 }}
              whileHover={{ y: -4, borderColor: 'rgba(168,67,151,0.4)' }}
            >
              {/* Header */}
              <div className="p-6 pb-4">
                <div className="flex items-center gap-3 mb-1">
                  <div className={`w-10 h-10 rounded-xl bg-black/30 border ${section.border} flex items-center justify-center`}>
                    <span className={`material-symbols-outlined ${section.iconColor} text-[22px]`}>{section.icon}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold font-headline text-white">{section.title}</h3>
                    <p className="text-xs text-on-surface-variant/50 font-label">{section.subtitle}</p>
                  </div>
                </div>
              </div>

              {/* Methods list */}
              <div className="px-6 pb-6">
                {section.methods.map((m, mi) => (
                  <motion.div
                    key={m.name}
                    className="flex items-center gap-3 py-3.5 border-b border-white/[0.04] last:border-0"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: si * 0.15 + mi * 0.05, duration: 0.3 }}
                  >
                    <div className="w-8 flex items-center justify-center flex-shrink-0">
                      <MethodIcon method={m} />
                    </div>
                    <span className="text-sm font-medium text-on-surface/90">{m.name}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
